import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, IconButton, Modal, TextField, Typography } from "@mui/material";
import { EditTable } from "../action-bill-modal/EditTable";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { RootState } from "../../../store";
import {
  addNewBillApi,
  getJDOpsCrops,
  getLandlordBillingEntities,
  getLandlordFieldTypes,
  resetLandlordFieldTypes,
} from "../../../slices/billingInvoices.slice";
import { AdhocProductsData, CreditTableData, ErrorObj } from "../action-bill-modal/types";
import {
  fetchAllFarmTypes,
  getCalendarMonth,
  getCalendarYears,
} from "../../../slices/serviceagreements.slice";
import {
  DropDownList,
  DropDownListChangeEvent,
} from "@progress/kendo-react-dropdowns";
import CloseIcon from "@mui/icons-material/Close";
import styles from "./Modal.module.scss";

import {
  DatePicker,
  DatePickerChangeEvent,
} from "@progress/kendo-react-dateinputs";

import CustomButton from "../../../components/Button";
import "./CustomDropdown.css";
import {
  adhocProductTableHeader,
  creditTableHeader,
  formatTableData,
  validateAdhocProductData,
  validateCreditData,
} from "../action-bill-modal/util";
import { round } from "lodash";
import moment from "moment";
import { ConfirmationDialog } from "../../../components/ConfirmationDialog/ConfirmationDialog";
import { Option } from "../../ServiceAgreements/types";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  height: "70%",
  width: "70%",
  bgcolor: "background.paper",
  border: "1px solid #000",
  boxShadow: 15,
  overflow: "scroll",
  align: "center",
};

export interface ModalProps {
  isOpen: any;
  onClose: any;
  title: string;
}

const AddBillComponent: React.FC<ModalProps> = ({ isOpen, onClose }) => {
  const [landlordId, setLandlordId] = useState<Option>();
  const [errorFields, setErrorFields] = useState<ErrorObj>({});
  const [monthId, setMonthId] = useState<Option>();
  const customTitleRef = useRef("");
  const [billingEntityId, setBillingEntityId] = useState<Option | null>();
  const [totalProductDue, setTotalProductDue] = useState<number>(0);
  const [totalCreditDue, setTotalCreditDue] = useState<number>(0);
  const [totalDue, setTotalDue] = useState(0);
  const [isDataValid, setDataValid] = useState(false);
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [billDate, setBillDate] = useState<Date | null>(new Date());
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [applicationProductData, setApplicationProductData] = useState<
    AdhocProductsData[]
  >([]);
  const [creditData, setCreditData] = useState<CreditTableData[]>([]);
  const { farmTypes, calendarMonths, calendarYears } = useAppSelector(
    (state: RootState) => state.serviceagreements
  );
  const { landlordBillingEntities } = useAppSelector(
    (state: RootState) => state.billingEntity
  );
  const defaultItem = { name: "Select" };
  const dispatch = useAppDispatch();

  const setCustomTitleonChange = (event: any) => {
    customTitleRef.current = event.target.value;
  };

  useEffect(() => {
    dispatch(fetchAllFarmTypes());
    dispatch(getJDOpsCrops());
    dispatch(getCalendarMonth(2025));
    dispatch(getCalendarYears());
    dispatch(resetLandlordFieldTypes());
  }, [dispatch]);

  useEffect(() => {
    const totalAmount = totalProductDue + totalCreditDue;
    setTotalDue(round(totalAmount, 2));
  }, [totalProductDue, totalCreditDue]);

  useEffect(() => {
    const isTableDataValid =
      !Object.keys(errorFields).length ||
      Object.keys(errorFields).every(
        (section: string) => errorFields[section].length === 0
      );
    const isValid =
      selectedYear &&
      monthId &&
      billDate &&
      landlordId &&
      billingEntityId &&
      customTitleRef.current &&
      applicationProductData.length &&
      isTableDataValid;
    setDataValid(!!isValid);
  }, [
    landlordId,
    billingEntityId,
    monthId,
    billDate,
    selectedYear,
    errorFields,
    applicationProductData,
  ]);

  const handleSaveDialogClose = () => setShowSaveDialog(false);
  const handleCancelDialogClose = () => setShowCancelDialog(false);

  const handleSaveYes = () => {
    setShowSaveDialog(false);
    handleSave();
  };

  const handleCancelYes = () => {
    setShowCancelDialog(false);
    onClose(false);
  };

  const onChangeLandlordName = (event: DropDownListChangeEvent) => {
    setBillingEntityId(null);
    const landlordId = event.value.id;
    dispatch(getLandlordFieldTypes({ id: [landlordId] }));
    dispatch(getLandlordBillingEntities({ id: [landlordId] }));
    resetFieldData(applicationProductData);
    setLandlordId(event.value);
  };

  const setBillingEntity = (event: DropDownListChangeEvent) => {
    setBillingEntityId(event.value);
  };

  const changeMonth = (event: DropDownListChangeEvent) => {
    setMonthId(event.value);
  };

  const handleSave = () => {
    const saveCustomInvoice = {
      landLordId: landlordId?.id,
      billingEntityId: billingEntityId?.id,
      month: monthId?.id,
      year: selectedYear,
      invoiceTitle: customTitleRef.current,
      billingDate: moment(billDate).format("MM/DD/YYYY"),
      adhocProducts: formatTableData(applicationProductData),
      creditRows: formatTableData(creditData),
    };

    dispatch(addNewBillApi(saveCustomInvoice)).then(() => {
      onClose(true);
    });
  };

  const handleYearChange = (event: DropDownListChangeEvent) => {
    setSelectedYear(event.target.value);
  };

  const handleCancel = (_event: React.SyntheticEvent, reason: string) => {
    if (reason === "backdropClick") {
      return;
    }
    onClose(false);
  };

  const resetFieldData = useCallback(
    (data: any) => {
      const updatedData = data.map((item: any) => ({
        ...item,
        fieldType: null,
      }));
      setApplicationProductData(updatedData);
    },
    [setApplicationProductData]
  );

  return (
    <Modal
      open={isOpen}
      onClose={handleCancel}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      sx={{ zIndex: 50 }}
    >
      <Box sx={modalStyle}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          m={1}
        >
          <Box flexGrow={1} display="flex" justifyContent="center">
            <Typography
              variant="h5"
              sx={{
                fontFamily: "Poppins, sans-serif",
                fontSize: "20px",
                fontWeight: 600,
                lineHeight: "26px",
              }}
              color="primary"
            >
              Add New Bill
            </Typography>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        <div style={{ padding: "0 15px 15px 15px" }}>
          <div style={{ marginBottom: "20px" }}>
            <form onSubmit={(e) => e.preventDefault()}>
              <fieldset>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: "2px",
                  }}
                >
                  <div className="dropdown-container">
                    <label htmlFor="custom-dropdown"> Select Year: </label>
                    <DropDownList
                      style={{
                        width: "230px",
                        height: "35px",
                      }}
                      data={calendarYears}
                      defaultValue={calendarYears[0] || defaultItem}
                      value={selectedYear}
                      onChange={handleYearChange}
                    />
                  </div>
                  <div className="dropdown-container">
                    <label htmlFor="custom-dropdown"> Month: </label>
                    <DropDownList
                      style={{
                        width: "230px",
                        height: "35px",
                      }}
                      textField="name"
                      dataItemKey="id"
                      data={calendarMonths}
                      defaultValue={defaultItem}
                      value={monthId}
                      onChange={changeMonth}
                    />
                  </div>
                  <div className="dropdown-container">
                    <label htmlFor="custom-dropdown"> Bill Date: </label>
                    <DatePicker
                      value={billDate}
                      onChange={(event: DatePickerChangeEvent) =>
                        setBillDate(event.value)
                      }
                      width={230}
                      inputAttributes={{ readOnly: true }}
                    />
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: "2px",
                  }}
                >
                  <div className="dropdown-container">
                    <label htmlFor="custom-dropdown"> Landlord: </label>
                    <DropDownList
                      style={{ width: "230px", height: "35px"}}
                      textField="name"
                      dataItemKey="id"
                      data={farmTypes}
                      defaultValue={defaultItem}
                      value={landlordId}
                      onChange={onChangeLandlordName}
                    />
                  </div>
                  <div className="dropdown-container">
                    <label htmlFor="custom-dropdown"> Billing Entity: </label>
                    <DropDownList
                      disabled={!landlordId}
                      style={{ width: "230px", height: "35px" }}
                      data={landlordBillingEntities}
                      textField="name"
                      dataItemKey="id"
                      defaultValue={defaultItem}
                      value={billingEntityId || defaultItem}
                      onChange={setBillingEntity}
                    />
                  </div>
                  <div
                    style={{ width: "230px", height: "35px" }}
                    className="dropdown-container"
                  ></div>
                </div>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: "2px",
                  }}
                >
                  <div
                    className="dropdown-container"
                    style={{
                      width: "460px",
                      maxWidth: "100%",
                      marginLeft: "21px",
                    }}
                  >
                    <label htmlFor="outlined-basic"> Bill Title </label>
                    <TextField
                      variant="outlined"
                      size="small"
                      onChange={setCustomTitleonChange}
                      defaultValue={customTitleRef.current}
                    />
                  </div>
                </div>
              </fieldset>
            </form>
          </div>
          <EditTable
            sectionName="adhocProducts"
            tableData={applicationProductData}
            headers={adhocProductTableHeader}
            setTableData={setApplicationProductData}
            totalDue={totalProductDue}
            setTotalDue={setTotalProductDue}
            validateFn={validateAdhocProductData}
            setErrorFieldsObj={setErrorFields}
          />
          <Box mt={3}>
            <Typography variant="h6" style={{ marginBottom: "10px" }}>
              Adjustments and Credit
            </Typography>
            <EditTable
              sectionName="creditTableData"
              headers={creditTableHeader}
              tableData={creditData}
              setTableData={setCreditData}
              totalDue={totalCreditDue}
              setTotalDue={setTotalCreditDue}
              setErrorFieldsObj={setErrorFields}
              validateFn={validateCreditData}
              totalLabel="Total Adjustments and Credits"
              columnWidths={{
                fieldType: 180,
                cropType: 160,
                date: 150,
                activity: 180,
                reasonForCredit: 280,
                totalDue: 150,
              }}
            />
          </Box>
          <Box sx={{ display: "flex", justifyContent: "right", gap: "1rem" }}>
            <div style={{ marginTop: "20px", fontSize: "16px" }}>
              <strong> Total Amount: {totalDue}</strong>
            </div>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "center", gap: "1rem" }}>
            <button
              onClick={() => setShowCancelDialog(true)}
              className={styles.cancelButton}
            >
              Cancel
            </button>
            <CustomButton
              onClick={() => setShowSaveDialog(true)}
              sx={{ width: "110px" }}
              disabled={!isDataValid}
            >
              Save
            </CustomButton>
          </Box>
          <ConfirmationDialog
            open={showSaveDialog}
            title="Save"
            description="Do you want to save the changes ?"
            handleNo={handleSaveDialogClose}
            handleYes={handleSaveYes}
          />

          {/* Cancel confirmation dialog */}
          <ConfirmationDialog
            open={showCancelDialog}
            title="Warning"
            description="Are you sure you want to cancel? Any unsaved changes will be lost."
            handleNo={handleCancelDialogClose}
            handleYes={handleCancelYes}
          />
        </div>
      </Box>
    </Modal>
  );
};

export default AddBillComponent;
